import React, { useState, useEffect } from "react";
import { Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";

export default function ManageLinks() {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchLinks = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${Base_URL}/api/links`);
            const data = await response.json();
            if (data.success) {
                setLinks(data.data);
            }
        } catch (error) {
            console.error("Error fetching links:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLinks();
    }, []);

    const handleUpdate = async (id, newUrl) => {
        if (!newUrl) {
            Swal.fire("Error", "URL cannot be empty", "error");
            return;
        }

        try {
            const response = await fetch(`${Base_URL}/api/links/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ url: newUrl })
            });
            const data = await response.json();

            if (data.success) {
                Swal.fire("Success", "Link updated successfully", "success");
                fetchLinks();
            } else {
                Swal.fire("Error", data.error || "Failed to update link.", "error");
            }
        } catch (error) {
            console.error("Error updating link:", error);
            Swal.fire("Error", "Failed to update link.", "error");
        }
    };

    return (
        <div className="flex flex-wrap mt-4 justify-center">
            <div className="relative z-10 w-full lg:w-8/12 px-4 shadow-lg rounded bg-dark-secondary text-white py-6 mb-12">
                <div className="rounded-t bg-dark-tertiary mb-4 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h6 className="text-white text-xl font-semibold">Manage Application Links</h6>
                    </div>
                </div>

                <div className="px-4 lg:px-10">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div className="flex flex-col space-y-6">
                            {links.map((link) => (
                                <LinkEditor
                                    key={link.id}
                                    link={link}
                                    onUpdate={(newUrl) => handleUpdate(link.id, newUrl)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const LinkEditor = ({ link, onUpdate }) => {
    const [currentUrl, setCurrentUrl] = useState(link.url);
    const [isEditing, setIsEditing] = useState(false);

    const handleSave = () => {
        onUpdate(currentUrl);
        setIsEditing(false);
    };

    return (
        <div className="bg-dark-tertiary rounded p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-white/5">
            <div className="flex-1">
                <h4 className="font-bold text-white mb-2">{link.title}</h4>
                {isEditing ? (
                    <input
                        type="url"
                        value={currentUrl}
                        onChange={(e) => setCurrentUrl(e.target.value)}
                        className="w-full border-0 px-3 py-2 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring"
                        placeholder="https://..."
                    />
                ) : (
                    <p className="text-gray-400 text-sm break-all">{link.url}</p>
                )}
            </div>
            <div className="flex shrink-0 gap-2">
                {isEditing ? (
                    <>
                        <button
                            onClick={handleSave}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow transition-all duration-150"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => {
                                setCurrentUrl(link.url);
                                setIsEditing(false);
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow transition-all duration-150"
                        >
                            Cancel
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow transition-all duration-150"
                    >
                        Edit Link
                    </button>
                )}
            </div>
        </div>
    );
};
